import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  getAlertTimeline, acknowledgeAlert, resolveAlert, reopenAlert, addAlertNote,
} from '../lib/adapters';
import { eventIcon, callInfo } from '../lib/eventVisual';
import { useI18n } from '../contexts/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { useCanManageAlerts, familyCanActOn } from '../lib/familyAlerts';
import { useAlertText, alertSubject, isAutoClosed, reasonText } from '../lib/alertText';
import { isImeiType } from '../lib/imei';
import { AlertClosure } from './alerts/AlertClosure';
import { WhyFired } from './alerts/WhyFired';
import { useRelativeTime, formatDateTime } from '../lib/time';
import { SeverityDot, StatusChip } from './StatusChip';
import {
  X, WarningCircle, EnvelopeSimple, CheckCircle, XCircle, Clock, Phone,
  PhoneX, NotePencil, ShieldCheck, DeviceMobile, Robot,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const SOS_ICON = L.divIcon({
  className: 'e2-sos',
  html: `<span style="display:flex;align-items:center;justify-content:center;width:22px;height:22px;border-radius:9999px;background:#D64533;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);color:#fff;font-size:7px;font-weight:800">SOS</span>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function deliveryVisual(status) {
  if (status === 'delivered' || status === 'sent') return { Icon: CheckCircle, cls: 'text-sage-600' };
  if (status === 'failed') return { Icon: XCircle, cls: 'text-terracotta-700' };
  return { Icon: Clock, cls: 'text-ink-muted' };
}

export function AlertDrawer({ alertId, alert: rowAlert = null, onClose, onChanged }) {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const rel = useRelativeTime();
  const text = useAlertText();
  const [data, setData] = useState(null);
  const canManage = useCanManageAlerts(data?.alert ?? rowAlert ?? null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [noteText, setNoteText] = useState('');

  const load = (silent = false) => {
    if (!alertId) return;
    if (!silent) setLoading(true);
    getAlertTimeline(alertId)
      .then((d) => setData(d))
      .catch(() => { if (!silent) setData(null); }) // keep last data on silent-refresh errors
      .finally(() => { if (!silent) setLoading(false); });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [alertId]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Fall back to the list row when the timeline can't be read (e.g. caregiver on a resident-less alert).
  const alert = data?.alert || rowAlert;
  // Live refresh: while the alert is still open (not resolved), poll the
  // timeline every 15s so new device events, calls, cancels and teammates'
  // notes stream in during an active SOS. Pauses when the tab is hidden.
  const isLive = !!data?.alert && data.alert.status !== 'resolved';
  useEffect(() => {
    if (!alertId || !isLive) return undefined;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load(true);
    }, 15000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alertId, isLive]);

  const trigger = data?.trigger_event;
  const events = Array.isArray(data?.events) ? data.events : [];
  const deviceNotes = Array.isArray(data?.notes) ? data.notes : [];
  const deliveries = Array.isArray(data?.deliveries) ? data.deliveries : [];
  const calls = events.filter((e) => e.type === 'call');
  const cancelledOnDevice = events.some((e) => e.type === 'sos_cancel')
    || deviceNotes.some((n) => n.event_type === 'sos_cancel');
  const gps = trigger?.meta?.gps;
  const imei = trigger?.meta?.imei;
  const alertType = alert?.event_type || trigger?.type;
  const message = alert?.message_params ? text(alert) : (trigger?.message || alert?.message);

  // Briefly flash event/note rows that arrive via the live poll. On first load
  // we just seed the baseline (no flash); subsequent unseen ids flash for ~3s.
  const seenRef = useRef(null);
  const [flashIds, setFlashIds] = useState(() => new Set());
  useEffect(() => { seenRef.current = null; setFlashIds(new Set()); }, [alertId]);
  useEffect(() => {
    if (!data) return;
    const ids = [
      ...events.map((e) => `e:${e.id}`),
      ...deviceNotes.map((n) => `n:${n.id}`),
    ];
    if (seenRef.current === null) { seenRef.current = new Set(ids); return; }
    const fresh = ids.filter((id) => !seenRef.current.has(id));
    seenRef.current = new Set(ids);
    if (fresh.length) {
      setFlashIds((prev) => new Set([...prev, ...fresh]));
      setTimeout(() => setFlashIds((prev) => {
        const n = new Set(prev); fresh.forEach((f) => n.delete(f)); return n;
      }), 3000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const doAck = async () => {
    setBusy(true);
    try { await acknowledgeAlert(alertId); toast.success(t('acknowledged')); onChanged?.(); load(); }
    catch { toast.error(t('generic_error')); }
    finally { setBusy(false); }
  };
  const doResolve = async () => {
    setBusy(true);
    try { await resolveAlert(alertId); toast.success(t('status_resolved')); onChanged?.(); load(); }
    catch { toast.error(t('generic_error')); }
    finally { setBusy(false); }
  };
  const doReopen = async () => {
    setBusy(true);
    try { await reopenAlert(alertId); toast.success(t('reopened')); onChanged?.(); load(); }
    catch { toast.error(t('generic_error')); }
    finally { setBusy(false); }
  };
  const doNote = async () => {
    const v = noteText.trim();
    if (!v) return;
    try {
      await addAlertNote(alertId, v);
      setNoteText('');
      onChanged?.();
      load();
      toast.success(t('note_saved'));
    } catch { toast.error(t('generic_error')); }
  };

  return (
    <div className="fixed inset-0 z-[1000]" data-testid="alert-drawer">
      <div
        className="absolute inset-0 bg-ink/30 backdrop-blur-[2px] animate-fade-in"
        onClick={onClose}
        data-testid="alert-drawer-backdrop"
      />
      <aside className="absolute right-0 top-0 h-full w-full max-w-md bg-bg-alt shadow-2xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-stone_line bg-white">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] uppercase tracking-[0.2em] text-sage-500 font-semibold">{t('alert_timeline')}</span>
              {isLive && (
                <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.14em] text-terracotta-700 font-semibold" data-testid="alert-drawer-live">
                  <span className="w-1.5 h-1.5 rounded-full bg-terracotta-600 animate-pulse-soft" />
                  {t('live')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <SeverityDot severity={alert?.severity} />
              <h2 className="font-heading text-xl text-ink truncate" data-testid="alert-drawer-subject">{alert ? alertSubject(alert, t) : '—'}</h2>
            </div>
            {alert && (
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <StatusChip status={alert.status === 'new' ? 'critical' : alert.status === 'acknowledged' ? 'warning' : 'ok'} label={t(`status_${alert.status}`)} />
                <span className="text-xs text-ink-muted">{formatDateTime(alert.created_at, lang)}</span>
              </div>
            )}
          </div>
          <button onClick={onClose} data-testid="alert-drawer-close" className="w-9 h-9 rounded-lg grid place-items-center text-ink-soft hover:bg-bg-alt">
            <X size={18} weight="bold" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 grid place-items-center text-ink-muted">{t('loading')}</div>
        ) : !data ? (
          alert ? (
            <div className="flex-1 overflow-y-auto px-6 py-5" data-testid="alert-drawer-fallback">
              <div className="bg-white rounded-xl border border-stone_line p-4">
                <div className="text-sm text-ink font-medium">{alertType ? t(`event_${alertType}`) : '—'}</div>
                <div className="text-xs text-ink-soft mt-1" data-testid="alert-drawer-message">{message}</div>
                <div className="text-xs text-ink-muted mt-1">{formatDateTime(alert.created_at, lang)} · {rel(alert.created_at)}</div>
                <AlertClosure alert={alert} testid="alert-drawer-closure" />
              </div>
            </div>
          ) : (
            <div className="flex-1 grid place-items-center text-ink-muted">{t('generic_error')}</div>
          )
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Device badge */}
            {trigger?.device_id && isImeiType(trigger?.device_type) && (
              <div className="inline-flex items-center gap-2 text-xs bg-white border border-stone_line rounded-full px-3 py-1.5" data-testid="alert-device-badge">
                <DeviceMobile size={14} className="text-sage-600" weight="duotone" />
                <span className="text-ink font-medium">{t('device_personal_alarm')}</span>
                {imei && <span className="text-ink-muted font-mono">· {imei}</span>}
              </div>
            )}

            {/* Cancelled on device banner */}
            {cancelledOnDevice && alert?.status !== 'resolved' && (
              <div className="flex items-start gap-3 rounded-xl border border-amber_warn-100 bg-amber_warn-100/40 px-4 py-3" data-testid="alert-cancelled-banner">
                <WarningCircle size={20} className="text-[#b4843f] mt-0.5 shrink-0" weight="fill" />
                <p className="text-sm text-ink-soft">{t('alert_cancelled_on_device')}</p>
              </div>
            )}

            {/* Trigger + mini-map */}
            <section>
              <SectionLabel>{t('alert_trigger')}</SectionLabel>
              <div className="bg-white rounded-xl border border-stone_line p-4">
                <div className="text-sm text-ink font-medium">{t(`event_${alertType}`)}</div>
                <div className="text-xs text-ink-soft mt-1" data-testid="alert-drawer-message">{message}</div>
                <div className="text-xs text-ink-muted mt-1">{formatDateTime(alert?.created_at || trigger?.created_at, lang)} · {rel(alert?.created_at || trigger?.created_at)}</div>
                {gps && (
                  <div className="mt-3 rounded-lg overflow-hidden border border-stone_line" style={{ height: 180 }}>
                    <MapContainer key={alertId} center={[gps.lat, gps.lng]} zoom={15} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
                      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                      <Marker position={[gps.lat, gps.lng]} icon={SOS_ICON}>
                        <Popup>{message}</Popup>
                      </Marker>
                    </MapContainer>
                  </div>
                )}
                {alert && <AlertClosure alert={alert} testid="alert-drawer-closure" />}
              </div>
            </section>

            <WhyFired type={alertType} meta={trigger?.meta} />

            {/* Notifications / deliveries */}
            <section>
              <SectionLabel>{t('delivery_status')}</SectionLabel>
              {deliveries.length === 0 ? (
                <div className="text-sm text-ink-muted">{t('alert_no_deliveries')}</div>
              ) : (
                <ul className="space-y-2" data-testid="alert-deliveries">
                  {deliveries.map((d, i) => {
                    const { Icon, cls } = deliveryVisual(d.status);
                    return (
                      <li key={i} className="flex items-center gap-3 bg-white rounded-xl border border-stone_line px-4 py-2.5 text-sm">
                        <EnvelopeSimple size={16} className="text-ink-soft" />
                        <span className="text-ink capitalize">{d.method}</span>
                        <span className="text-ink-muted font-mono text-xs truncate flex-1">{d.recipient}</span>
                        <Icon size={16} className={cls} weight="fill" />
                        <span className="text-xs text-ink-muted whitespace-nowrap">{rel(d.sent_at)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>

            {/* Calls */}
            {calls.length > 0 && (
              <section>
                <SectionLabel>{t('alert_calls')}</SectionLabel>
                <ul className="space-y-2" data-testid="alert-calls">
                  {calls.map((ev) => {
                    const c = callInfo(ev);
                    return (
                      <li key={ev.id} className="flex items-center gap-3 bg-white rounded-xl border border-stone_line px-4 py-2.5 text-sm">
                        {c?.answered ? <Phone size={16} className="text-sage-600" weight="fill" /> : <PhoneX size={16} className="text-terracotta-700" weight="fill" />}
                        <span className="text-ink font-mono">{c?.number || '—'}</span>
                        <span className={`text-xs ${c?.answered ? 'text-sage-700' : 'text-terracotta-700'}`}>
                          {c?.answered ? t('call_answered') : t('call_missed')}
                        </span>
                        {c?.durationS != null && c.answered && <span className="text-xs text-ink-muted">· {c.durationS}s</span>}
                        <span className="text-xs text-ink-muted ml-auto whitespace-nowrap">{rel(c?.at)}</span>
                      </li>
                    );
                  })}
                </ul>
              </section>
            )}

            {/* Full event timeline */}
            {events.length > 0 && (
              <section>
                <SectionLabel>{t('recent_events')}</SectionLabel>
                <ol className="relative border-l border-stone_line ml-2">
                  {events.map((ev) => {
                    const Icon = eventIcon(ev.type);
                    return (
                      <li key={ev.id} className={`ml-5 pb-4 last:pb-0 rounded-lg ${flashIds.has(`e:${ev.id}`) ? 'animate-highlight' : ''}`}>
                        <span className="absolute -left-[7px] w-3.5 h-3.5 rounded-full bg-white border-2 border-stone_line" />
                        <div className="flex items-start gap-2">
                          <Icon size={16} className="text-ink-soft mt-0.5" weight="duotone" />
                          <div className="min-w-0">
                            <div className="text-sm text-ink">{t(`event_${ev.type}`)}</div>
                            <div className="text-xs text-ink-muted">{rel(ev.created_at)}</div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </section>
            )}

            {/* Notes (device + caregiver) */}
            <section>
              <SectionLabel>{t('notes')}</SectionLabel>
              <ul className="space-y-2 mb-3" data-testid="alert-drawer-notes">
                {deviceNotes.map((n) => (
                  n.source === 'system' ? (
                    <li key={n.id} data-testid={`alert-note-system-${n.id}`} className={`text-xs bg-bg-alt rounded-lg border border-dashed border-stone_line px-3 py-2 flex items-start gap-1.5 ${flashIds.has(`n:${n.id}`) ? 'animate-highlight' : ''}`}>
                      <Robot size={13} className="text-ink-muted mt-0.5 shrink-0" weight="duotone" />
                      <span className="text-ink-soft">
                        {n.event_type === 'resolved' && isAutoClosed(alert) && alert?.resolution_reason
                          ? t('al_auto_closed_reason', { reason: reasonText(alert.resolution_reason, t) })
                          : n.note}
                      </span>
                      <span className="text-ink-muted ml-auto whitespace-nowrap">{rel(n.created_at)}</span>
                    </li>
                  ) : (
                  <li key={n.id} className={`text-xs bg-white rounded-lg border px-3 py-2 ${n.source === 'caregiver' ? 'border-sage/30' : 'border-stone_line'} ${flashIds.has(`n:${n.id}`) ? 'animate-highlight' : ''}`}>
                    {n.author_name && <span className="text-sage-700 font-medium mr-1">{n.author_name}:</span>}
                    <span className="text-ink">{n.note}</span>
                    <span className="text-ink-muted ml-2">· {rel(n.created_at)}</span>
                  </li>
                  )
                ))}
                {deviceNotes.length === 0 && (
                  <li className="text-sm text-ink-muted">{t('no_results')}</li>
                )}
              </ul>
              {canManage && (
                <div className="flex gap-2">
                  <input
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && doNote()}
                    placeholder={t('note_placeholder')}
                    data-testid="alert-drawer-note-input"
                    className="flex-1 px-3 py-2 rounded-lg border border-stone_line bg-white focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage-100 text-sm"
                  />
                  <button onClick={doNote} data-testid="alert-drawer-note-save" className="px-3 py-2 rounded-lg bg-white border border-stone_line text-ink-soft hover:bg-bg-alt text-sm inline-flex items-center gap-1.5">
                    <NotePencil size={14} /> {t('save_note')}
                  </button>
                </div>
              )}
            </section>
          </div>
        )}

        {/* Footer actions */}
        {alert && !canManage && user?.role === 'caregiver' && !familyCanActOn(alert) && (
          <div className="border-t border-stone_line bg-white px-6 py-4 text-xs text-ink-soft" data-testid="alert-drawer-admin-only">
            {t('al_admin_only_ack')}
          </div>
        )}
        {alert && canManage && (
          <div className="border-t border-stone_line bg-white px-6 py-4 flex gap-3">
            {!alert.acknowledged && alert.status === 'new' && (
              <button onClick={doAck} disabled={busy} data-testid="alert-drawer-ack" className="flex-1 bg-sage text-white hover:bg-sage-600 disabled:opacity-50 px-4 py-2.5 rounded-xl text-sm font-medium inline-flex items-center justify-center gap-2">
                <ShieldCheck size={16} weight="bold" /> {t('acknowledge')}
              </button>
            )}
            {alert.status !== 'resolved' ? (
              <button onClick={doResolve} disabled={busy} data-testid="alert-drawer-resolve" className="flex-1 border border-sage/40 text-sage-700 hover:bg-sage-100 disabled:opacity-50 px-4 py-2.5 rounded-xl text-sm font-medium">
                {t('resolve')}
              </button>
            ) : (
              <button onClick={doReopen} disabled={busy} data-testid="alert-drawer-reopen" className="flex-1 border border-stone_line text-ink-soft hover:bg-bg-alt disabled:opacity-50 px-4 py-2.5 rounded-xl text-sm font-medium">
                {t('reopen')}
              </button>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.2em] text-ink-muted font-semibold mb-2">{children}</div>;
}
